import {
  Body,
  Box,
  Heading,
  useBreakpointValue,
  useTheme,
} from '@hover/blueprint';
import type { BoxProps } from '@hover/blueprint/chakra';
import { iconShare } from '@hover/icons';
import React, { useMemo } from 'react';
import { generatePath } from 'react-router';

import { ProjectCardSection } from 'src/modules/homeownerApp/components/Projects/Project/ProjectCardSection';
import {
  useHomeownerAppContext,
  useShareToast,
} from 'src/modules/homeownerApp/hooks';
import type { HomeownerAppTrackingData } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import type { FullJson } from 'src/modules/paintMeasurements/components/types';
import { useIsMobileSize } from 'src/modules/renderings/hooks';
import { Button } from 'src/shared/components';
import { copyLinkViaBrowserAPI } from 'src/shared/helpers/clipboard';
import { hasNativeShare } from 'src/shared/helpers/environment';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { ViewerVersion, createJobShare } from 'src/shared/requests';
import { Route as Routes } from 'src/shared/route';

import { FocusElement } from '../../ThreeDModel/machete';

type MeasurementsProps = {
  fullJson?: string | null;
  jobId: number;
  projectId?: number;
  setActiveItem: (title: MeasurementTitle | null) => void;
  activeItem: MeasurementTitle | null;
} & BoxProps;

export const MEASUREMENT_FOCUS_ELEMENTS = {
  presentation: FocusElement.WALL,
  fascia: FocusElement.FASCIA,
  soffit: FocusElement.SOFFIT,
  trim: FocusElement.TRIM,
  doors: FocusElement.DOOR,
  windows: FocusElement.WINDOW,
  shutters: FocusElement.SHUTTERS,
  roof: FocusElement.ROOF,
  pitches: FocusElement.ROOF,
};

export type MeasurementTitle = keyof typeof MEASUREMENT_FOCUS_ELEMENTS;

interface MeasurementItemProps {
  title: MeasurementTitle;
  value: string | number;
  active?: boolean;
  onClick: (title: MeasurementTitle) => void;
  trackingData: HomeownerAppTrackingData;
}

export const MeasurementItem = ({
  title,
  value,
  active,
  onClick,
  trackingData,
}: MeasurementItemProps) => {
  const { space } = useTheme();
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const columnCount = useBreakpointValue({ base: 1, tablet: 2, desktop: 3 });

  return (
    <Button
      _hover={{
        borderColor: 'neutral.900',
      }}
      borderColor={active ? 'neutral.900' : 'neutral.200'}
      borderRadius={12}
      borderWidth={active ? '2.5px' : 'unset'}
      h="92px"
      label={`${t(title)}: ${value}`}
      onClick={() => onClick(title)}
      optionalTrackingProperties={{
        ...trackingData,
        button_location: 'Measurement tiles',
        track_id: 'key-measurement-buttons',
      }}
      p={400}
      sx={{
        '& .button-label': {
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          gap: 300,
        },
      }}
      textAlign="left"
      variant="tertiary"
      width={{
        base: '176px',
        tablet: `calc((100% / ${columnCount}) - (((${columnCount} - 1) / ${columnCount}) * ${space[200]}))`,
      }}
    >
      <Body
        color={active ? 'neutral.900' : 'neutral.400'}
        m={0}
        p={0}
        textStyle="bodySmall"
      >
        {t(title)}
      </Body>
      <Heading m={0} p={0} textStyle="headingMedium">
        {value}
      </Heading>
    </Button>
  );
};

export const Measurements = ({
  fullJson,
  jobId,
  projectId,
  setActiveItem,
  activeItem,
  ...props
}: MeasurementsProps) => {
  const { trackingData } = useHomeownerAppContext();
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const isMobileSize = useIsMobileSize();

  const toast = useShareToast();
  const scanTrackingData = useMemo(
    () => ({
      ...trackingData,
      button_location: 'Measurement tile',
      job_id: jobId,
      project_id: projectId,
    }),
    [trackingData, jobId, projectId],
  );
  const parsedFullJson = useMemo(
    () => (fullJson ? (JSON.parse(fullJson) as FullJson) : null),
    [fullJson],
  );
  const measurementsSummary = parsedFullJson?.summary
    ? Object.entries(parsedFullJson?.summary)
    : [];
  const handleItemClick = (title: MeasurementTitle) => {
    setActiveItem(title);
  };

  const handleViewReports = () => {
    window.open(
      Routes.HOMEOWNER_MEASUREMENTS.path.replace(':jobId', String(jobId)),
      '_blank',
    );
  };

  const handleShare = async () => {
    const jobShare = await createJobShare(
      jobId,
      ViewerVersion.PAINT_MEASUREMENTS,
    );

    if (!jobShare?.token) {
      toast({
        description: t('share.messages.copyLink.fail'),
        status: 'error',
      });
      return;
    }

    const shareUrl = new URL(
      generatePath(Routes.PAINT_MEASUREMENTS_SHARED.path, {
        jobId,
        shareToken: jobShare.token,
      }),
      window.location.origin,
    ).href;

    try {
      if (hasNativeShare()) {
        await navigator.share({ url: shareUrl });
      } else {
        await copyLinkViaBrowserAPI(shareUrl);
      }
      toast({
        description: t('share.messages.copyLink.success'),
        status: 'success',
      });
    } catch (e) {
      toast({
        description: t('share.messages.copyLink.fail'),
        status: 'error',
      });
    }
  };
  return (
    <>
      <ProjectCardSection gap={200} m={0} {...props}>
        {measurementsSummary?.length
          ? measurementsSummary.map(([title, value]) => {
              const typedTitle = title as MeasurementTitle;
              const typedValue = value as string | number;
              return (
                <MeasurementItem
                  active={activeItem === typedTitle}
                  key={`${typedTitle}-${typedValue}`}
                  onClick={handleItemClick}
                  title={typedTitle}
                  trackingData={scanTrackingData}
                  value={typedValue}
                />
              );
            })
          : null}
      </ProjectCardSection>
      <Box gap={200} {...props}>
        <Button
          flex={{ base: 1, desktop: 0 }}
          includeTrackingImpression
          label={t('projects.keyMeasurements.viewFullReport')}
          onClick={handleViewReports}
          optionalTrackingProperties={{
            ...scanTrackingData,
            track_id: 'view-full-measurements-report',
          }}
          size="large"
        >
          {t('projects.keyMeasurements.viewFullReport')}
        </Button>
        <Button
          flex={{ tablet: 1, desktop: 0 }}
          iconAfter={iconShare}
          includeTrackingImpression
          label={t('projects.keyMeasurements.share')}
          onClick={handleShare}
          optionalTrackingProperties={{
            ...scanTrackingData,
            track_id: 'share-full-measurements-report',
          }}
          size="large"
        >
          {!isMobileSize && t('projects.keyMeasurements.share')}
        </Button>
      </Box>
    </>
  );
};
